import { CLIENT_PAGES } from '@/components/client/constants/pages'
import { normalizeClientId } from '@/utilities/auth/authHelper'
import {
  buildProgramDashboardRedirectUrl,
  isAuthorizedOrgParam,
  resolveProgramPartnerIdFromAllowedPartners,
} from '@/utilities/dashboard/programDashboardUrl'
import type { IdentityScopes } from '@/utilities/auth/identityScopeRoles'
import {
  buildOrgDashboardUrl,
  buildProgramContextDashboardUrl,
} from '@/utilities/dashboard/orgProgramContextUrl'
import {
  resolveOrgContextSelection,
  type SwitcherOrganization,
  type SwitcherSelection,
} from '@/utilities/dashboard/orgProgramSwitcherTree'
import { resolveClientPortalRouteIntent } from '@/utilities/client/clientPortalContext'

/**
 * Dashboard context precedence:
 * 1. resolveClientPortalRouteIntent reads URL intent (`partners` wins over `org`).
 * 2. resolveDashboardRouteState combines that intent with access and the org tree.
 * 3. ClientPortalContextProvider publishes the resulting route, switcher, and nav state.
 * 4. The provider applies redirects and selected-client synchronization once.
 * Pages and navbar consumers render the provider result without reinterpreting the URL.
 */

export type OrgDashboardAccessInput = {
  isFlagReady: boolean
  isReady: boolean
  isFlagEnabled: boolean
  canViewOrgDashboard: boolean
  identityScopes?: IdentityScopes | null
}

export type ProgramDashboardSource =
  | { kind: 'loading' }
  | { kind: 'unavailable' }
  | { kind: 'program'; partnerId: string; canonicalHref: string }

export type DashboardRouteTarget = {
  href: string
  programId: string | null
}

export type DashboardRouteState =
  | { kind: 'loading' }
  | { kind: 'unavailable' }
  | { kind: 'redirect'; target: DashboardRouteTarget }
  | { kind: 'program'; partnerId: string }
  | { kind: 'org'; selection: Extract<SwitcherSelection, { mode: 'org' }> }

export function resolveProgramDashboardSource({
  searchParams,
  selectedClientId,
  allowedPartnerIds,
  isLoading = false,
  loadFailed = false,
}: {
  searchParams: URLSearchParams
  selectedClientId: string | null | undefined
  allowedPartnerIds: readonly string[]
  isLoading?: boolean
  loadFailed?: boolean
}): ProgramDashboardSource {
  if (isLoading) return { kind: 'loading' }
  if (loadFailed) return { kind: 'unavailable' }

  const partnerId = resolveProgramPartnerIdFromAllowedPartners(
    allowedPartnerIds,
    searchParams.get('partners'),
    selectedClientId,
  )
  if (!partnerId) return { kind: 'unavailable' }

  return {
    kind: 'program',
    partnerId,
    canonicalHref: buildProgramDashboardRedirectUrl(searchParams, partnerId),
  }
}

function buildProgramTarget(programId: string): DashboardRouteTarget {
  return {
    href: buildProgramContextDashboardUrl(programId),
    programId,
  }
}

export function buildDashboardHrefFromSelection(
  selection: SwitcherSelection | null,
): string | null {
  if (selection?.mode === 'program') {
    return buildProgramTarget(selection.programId).href
  }

  if (selection?.mode === 'org') {
    return buildOrgDashboardUrl(selection.primaryPartnerId)
  }

  return null
}

function buildTargetFromSelection(
  selection: SwitcherSelection | null,
): DashboardRouteTarget | null {
  const href = buildDashboardHrefFromSelection(selection)
  if (!href) return null

  return {
    href,
    programId: selection?.mode === 'program' ? selection.programId : null,
  }
}

function resolveOrgDashboardTarget({
  organizations,
  orgParam,
  selectedClientId,
}: {
  organizations: readonly SwitcherOrganization[]
  orgParam: string | null | undefined
  selectedClientId: string | null | undefined
}):
  | { kind: 'current'; selection: Extract<SwitcherSelection, { mode: 'org' }> }
  | { kind: 'redirect'; target: DashboardRouteTarget }
  | { kind: 'unresolved' } {
  const normalizedOrgParam = orgParam?.trim() ? normalizeClientId(orgParam.trim()) : null
  const selection = resolveOrgContextSelection({ organizations, orgParam, selectedClientId })
  const target = buildTargetFromSelection(selection)

  if (!selection || !target) {
    return { kind: 'unresolved' }
  }

  if (selection.mode === 'org' && selection.primaryPartnerId === normalizedOrgParam) {
    return { kind: 'current', selection }
  }

  return { kind: 'redirect', target }
}

/**
 * Dashboard page routing state for `/client/dashboard`.
 * Owns the org-tree-dependent redirects so the page, switcher label, and nav
 * disable logic share the same route contract.
 */
export function resolveDashboardRouteState({
  searchParams,
  selectedClientId,
  access,
  organizations,
  programPartnerId,
  programDataUnavailable = false,
  orgsFetchSettled,
  orgsLoadFailed,
  orgsLoading,
}: {
  searchParams: URLSearchParams | { toString(): string }
  selectedClientId: string | null | undefined
  access: OrgDashboardAccessInput
  organizations: readonly SwitcherOrganization[]
  programPartnerId: string | null
  programDataUnavailable?: boolean
  orgsFetchSettled: boolean
  orgsLoadFailed: boolean
  orgsLoading: boolean
}): DashboardRouteState {
  const params = new URLSearchParams(searchParams.toString())
  const routeIntent = resolveClientPortalRouteIntent({
    pathname: CLIENT_PAGES.dashboard,
    searchParams: params,
    selectedClientId,
  })

  if (!access.isFlagReady) {
    return { kind: 'loading' }
  }

  if (programDataUnavailable || !programPartnerId) return { kind: 'unavailable' }

  if (!access.isFlagEnabled) {
    return {
      kind: 'program',
      partnerId: programPartnerId,
    }
  }

  if (!access.isReady) {
    return { kind: 'loading' }
  }

  const orgPartnerId = routeIntent.kind === 'org' ? routeIntent.partnerId : null
  const hasInvalidOrgParam =
    orgPartnerId !== null && isAuthorizedOrgParam(orgPartnerId, access.identityScopes) === false
  const shouldStayOnOrgShell =
    access.canViewOrgDashboard && routeIntent.kind !== 'program' && !hasInvalidOrgParam

  if (!shouldStayOnOrgShell) {
    return {
      kind: 'redirect',
      target: {
        href: buildProgramDashboardRedirectUrl(params, programPartnerId),
        programId: programPartnerId,
      },
    }
  }

  if (!orgsLoadFailed && (orgsLoading || !orgsFetchSettled)) {
    return { kind: 'loading' }
  }

  const programFallbackHref = buildProgramDashboardRedirectUrl(params, programPartnerId)
  if (orgsLoadFailed || organizations.length === 0) {
    return {
      kind: 'redirect',
      target: { href: programFallbackHref, programId: programPartnerId },
    }
  }

  const resolution = resolveOrgDashboardTarget({
    organizations,
    orgParam: params.get('org'),
    selectedClientId,
  })
  if (resolution.kind === 'unresolved') {
    return {
      kind: 'redirect',
      target: { href: programFallbackHref, programId: programPartnerId },
    }
  }
  if (resolution.kind === 'redirect') {
    return { kind: 'redirect', target: resolution.target }
  }

  return { kind: 'org', selection: resolution.selection }
}

/** True when pathname is the org multi-program dashboard shell (not `/program`). */
export function isOrgDashboardPath(pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, '') || '/'
  return normalized === CLIENT_PAGES.dashboard
}

/** Dashboard nav remains active for both org and single-program dashboard routes. */
export function isDashboardNavPath(pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, '') || '/'
  return normalized === CLIENT_PAGES.dashboard || normalized === CLIENT_PAGES.programDashboard
}

type DashboardNavigationInput = {
  pathname: string
  searchParams: URLSearchParams
  access: OrgDashboardAccessInput
  routeState: DashboardRouteState | null
}

export function resolveDashboardNavigationState({
  pathname,
  searchParams,
  access,
  routeState,
}: DashboardNavigationInput): {
  isOrgContext: boolean
  isProgramScopedNavDisabled: boolean
} {
  if (!isOrgDashboardPath(pathname) || routeState === null) {
    return { isOrgContext: false, isProgramScopedNavDisabled: false }
  }

  if (routeState.kind === 'org') {
    // Program-scoped nav is only ambiguous when the org has more than one program to mean.
    return {
      isOrgContext: true,
      isProgramScopedNavDisabled: routeState.selection.programIds.length > 1,
    }
  }

  if (routeState.kind === 'redirect') {
    const target = new URL(routeState.target.href, 'https://client.local')
    const targetIntent = resolveClientPortalRouteIntent({
      pathname: target.pathname,
      searchParams: target.searchParams,
      selectedClientId: routeState.target.programId,
    })
    const isOrgContext = targetIntent.kind === 'org'
    return { isOrgContext, isProgramScopedNavDisabled: isOrgContext }
  }

  if (routeState.kind === 'unavailable') {
    return { isOrgContext: false, isProgramScopedNavDisabled: true }
  }

  if (routeState.kind !== 'loading') {
    return { isOrgContext: false, isProgramScopedNavDisabled: false }
  }

  const hasProgramIntent =
    resolveClientPortalRouteIntent({
      pathname,
      searchParams,
      selectedClientId: null,
    }).kind === 'program'
  const isProgramScopedNavDisabled =
    !hasProgramIntent && (!access.isFlagReady || access.isFlagEnabled)

  return { isOrgContext: false, isProgramScopedNavDisabled }
}
